/*
 * 'electron' import 의 대체물 (vue.config.js alias: electron → 이 파일).
 *
 * 분리 전(내재화)엔 nodeIntegration:true 라 렌더러가 require('electron') 의 ipcRenderer 를
 * 직접 썼다. 분리 후엔 contextIsolation:true 라 직접 접근이 막히고, preload(ipcPreload.js)가
 * contextBridge 로 window.electron.ipcRenderer 를 주입한다 (React saas-web 과 동일 방식).
 * 이 어댑터는 그 window.electron.ipcRenderer 로 위임한다.
 *
 * 일렉트론에서는 preload 브리지를 쓴다. 비운영 빌드에서 일렉트론 없이 React 창이 window.open 으로
 * 띄운 경우에만 postMessage 기반 WebIpcBridge 로 대체한다 (운영 빌드는 허용 origin 이 비어 있어 생성되지 않음).
 * preload 주입 전(초기 로드 순간) 호출에 대비해 옵셔널 체이닝으로 방어한다.
 */

import packageConfig from '@package'
import { createWebBridge } from './web-bridge/create-web-bridge'
import { resolveAllowedHostOrigins } from './web-bridge/host-origins'

type Listener = (...args: never[]) => void

export type Bridge = {
    send(channel: string, payload?: unknown): void
    on(channel: string, listener: Listener): void
    removeListener(channel: string, listener: Listener): void
    removeAllListeners(channel: string): void
}

declare global {
    interface Window {
        electron?: { ipcRenderer?: Bridge; windowName?: string }
    }
}

// null = 아직 판정 전, undefined = 웹 브리지 사용 안 함
let webBridge: Bridge | undefined | null = null

const resolveWebBridge = (): Bridge | undefined => {
    if (webBridge === null) {
        webBridge = createWebBridge(window, resolveAllowedHostOrigins({
            isDevelopment: packageConfig.isDevelopment,
            isLocal: process.env.AFTERDOC_LOCAL === 'true',
            webPort: process.env.AFTERDOC_WEB_PORT || undefined,
        }))
    }
    return webBridge
}

/** 운영 브리지 판정: preload 의 window.electron.ipcRenderer, 없으면(비운영 웹) postMessage 브리지. */
export const resolveBridge = (): Bridge | undefined => {
    if (typeof window === 'undefined') { return undefined }
    return window.electron?.ipcRenderer ?? resolveWebBridge()
}

/**
 * bridge 를 매 호출마다 다시 읽는 ipcRenderer 어댑터를 만든다.
 * 운영은 window.electron.ipcRenderer 를, 샌드박스 빌드(electronStub.e2e.ts)는 테스트가 끼운 bridge 를 먼저 본다.
 */
export const createIpcRenderer = (bridge: () => Bridge | undefined) => ({
    send: (channel: string, payload?: unknown): void => bridge()?.send(channel, payload),
    on: (channel: string, listener: Listener): void => bridge()?.on(channel, listener),
    addListener: (channel: string, listener: Listener): void => bridge()?.on(channel, listener),
    removeListener: (channel: string, listener: Listener): void =>
        bridge()?.removeListener(channel, listener),
    removeAllListeners: (channel: string): void => bridge()?.removeAllListeners(channel),

    /** preload 브릿지엔 once 가 없어 on + removeListener 로 에뮬레이트한다 */
    once: (channel: string, listener: Listener): void => {
        const b = bridge()
        if (b == undefined) { return }
        const wrapper = ((...args: never[]) => {
            b.removeListener(channel, wrapper)
            listener(...args)
        }) as Listener
        b.on(channel, wrapper)
    },
})

export const ipcRenderer = createIpcRenderer(resolveBridge)

const electronStub = { ipcRenderer }

export default electronStub
